/**
 * Connects "someone searched" (logged in knowledgeBaseSearch.ts) to "and
 * then they clicked THIS result", so the admin page can show what share of
 * searches led anywhere -- the number that says whether search is actually
 * helping people use the site.
 *
 * Kept deliberately tiny and free of heavy imports: the search UI surfaces
 * (header, homepage, side panel, command palette) all import it, and the
 * search corpus itself is lazy-loaded so it only downloads for people who
 * actually search.
 *
 * Only the FIRST click after a given search is recorded -- "did this
 * search lead somewhere, and where" -- not every click.
 */
export type SearchClickKind =
  | "tool"
  | "pipeline"
  | "persona"
  | "blog"
  | "faq"
  | "assistant_option"
  | "assistant_tool"
  | "assistant_workflow"
  | "enter";

let currentSearchLog: Promise<number | null> = Promise.resolve(null);
const alreadyClicked = new Set<number>();

/** Called by the search when it starts logging: the promise resolves to the
 * stored row's ID (or null if logging failed, in which case clicks for
 * that search are simply not recorded). */
export function setCurrentSearchLog(pending: Promise<number | null>): void {
  currentSearchLog = pending;
}

/** Fire-and-forget. Never throws, never blocks navigation. `position` is the
 * 0-based place of the clicked result within its list. */
export function recordSearchClick(href: string, kind: SearchClickKind, position: number): void {
  currentSearchLog
    .then(async (searchLogId) => {
      if (!searchLogId || alreadyClicked.has(searchLogId)) return;
      alreadyClicked.add(searchLogId);
      const [{ trpcVanilla }, { getOrCreateDeviceId }] = await Promise.all([
        import("@/lib/trpcVanilla"),
        import("@/lib/deviceId"),
      ]);
      await trpcVanilla.search.click.mutate({
        searchLogId,
        href: href.slice(0, 160),
        kind,
        position: Math.max(0, Math.min(50, Math.floor(position))),
        deviceId: getOrCreateDeviceId(),
      });
    })
    .catch(() => {
      /* Best-effort only -- never surfaced. */
    });
}
